import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { loadStripe } from '@stripe/stripe-js'

import {
  getPublicMenu,
  getPublicOrderStatus,
  submitPublicMenuOrder,
  createPublicMenuPaymentIntent,
  getPublicMenuPricing,
} from '../../utilities/api'

import MenuPayment from './MenuPayment'
import MenuPricingBreakdown from './MenuPricingBreakdown'

import './menuWrapper.scss'

const STATUS_STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'On the way' },
  { key: 'delivered', label: 'Delivered' },
]

const PICKUP_STATUS_STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'Ready for pickup' },
  { key: 'delivered', label: 'Picked up' },
]

const STATUS_POLL_MS = 10000

function getActiveOrderStorageKey(
  organizationUuid,
  menuKey,
  venueUuid,
  eventUuid
) {
  return `menu-active-order:${organizationUuid}:${venueUuid || '~'}:${eventUuid || '~'}:${menuKey}`
}

function formatCurrency(value) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value ?? 0))
}

function resolveMediaUrl(url) {
  if (!url) return ''
  if (url.startsWith('http')) return url
  if (url.startsWith('/')) {
    const base = process.env.REACT_APP_API?.replace(/\/api\/?$/, '') || ''
    return `${base}${url}`
  }
  return url
}

function buildCartKey(itemId) {
  return String(itemId)
}

function getStatusStepIndex(status) {
  const index = STATUS_STEPS.findIndex((step) => step.key === status)
  return index >= 0 ? index : 0
}

export default function MenuWrapper({
  organizationUuid,
  menuKey,
  venueUuid = '',
  eventUuid = '',
  rowName,
  seatName,
  fulfillmentType = 'seat_delivery',
  locationName = '',
  initialOrderNumber = '',
}) {
  const isPickup = fulfillmentType === 'pickup'
  const statusSteps = isPickup ? PICKUP_STATUS_STEPS : STATUS_STEPS
  const menuRow = rowName || '-'
  const menuSeat = seatName || '-'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [menu, setMenu] = useState(null)
  const [activeCategory, setActiveCategory] = useState('all')
  const [cart, setCart] = useState({})
  const [cartOpen, setCartOpen] = useState(false)
  const [checkoutStep, setCheckoutStep] = useState('review')
  const [submitting, setSubmitting] = useState(false)
  const [loadingPayment, setLoadingPayment] = useState(false)
  const [clientSecret, setClientSecret] = useState('')
  const [paymentIntentId, setPaymentIntentId] = useState('')
  const [stripePromise, setStripePromise] = useState(null)
  const [pricing, setPricing] = useState(null)
  const [pricingLoading, setPricingLoading] = useState(false)
  const [orderConfirmation, setOrderConfirmation] = useState(null)
  const [activeOrder, setActiveOrder] = useState(null)

  useEffect(() => {
    setLoading(true)
    setError('')
    getPublicMenu(organizationUuid, menuKey, menuRow, menuSeat, {
      venueUuid,
      eventUuid,
    })
      .then((res) => {
        setMenu(res.data)
      })
      .catch((err) => {
        console.error(err)
        setError('Unable to load the menu right now. Please try again shortly.')
      })
      .finally(() => setLoading(false))
  }, [organizationUuid, menuKey, venueUuid, eventUuid, menuRow, menuSeat])

  useEffect(() => {
    const storageKey = getActiveOrderStorageKey(
      organizationUuid,
      menuKey,
      venueUuid,
      eventUuid
    )
    const remembered = initialOrderNumber || sessionStorage.getItem(storageKey)
    if (!remembered) return

    getPublicOrderStatus(organizationUuid, remembered, {
      section: menuKey,
      row: isPickup ? undefined : rowName,
      seat: isPickup ? undefined : seatName,
      venueUuid,
      eventUuid,
    })
      .then((res) => {
        const status = res.data
        if (
          status?.fulfillmentStatus &&
          !['delivered', 'cancelled'].includes(status.fulfillmentStatus)
        ) {
          setActiveOrder(status)
        } else {
          sessionStorage.removeItem(storageKey)
        }
      })
      .catch(() => {
        sessionStorage.removeItem(storageKey)
      })
  }, [
    organizationUuid,
    menuKey,
    venueUuid,
    eventUuid,
    rowName,
    seatName,
    initialOrderNumber,
    isPickup,
  ])

  useEffect(() => {
    if (!activeOrder?.orderNumber) return undefined

    const pollStatus = () => {
      getPublicOrderStatus(organizationUuid, activeOrder.orderNumber, {
        section: menuKey,
        row: isPickup ? undefined : rowName,
        seat: isPickup ? undefined : seatName,
        venueUuid,
        eventUuid,
      })
        .then((res) => {
          const status = res.data
          if (['delivered', 'cancelled'].includes(status?.fulfillmentStatus)) {
            setActiveOrder(status)
            sessionStorage.removeItem(
              getActiveOrderStorageKey(
                organizationUuid,
                menuKey,
                venueUuid,
                eventUuid
              )
            )
            return
          }
          setActiveOrder(status)
        })
        .catch((err) => {
          console.error(err)
        })
    }

    pollStatus()
    const timer = setInterval(pollStatus, STATUS_POLL_MS)
    return () => clearInterval(timer)
  }, [
    activeOrder?.orderNumber,
    organizationUuid,
    menuKey,
    venueUuid,
    eventUuid,
    rowName,
    seatName,
    isPickup,
  ])

  const categories = useMemo(() => {
    const list = menu?.categories ?? []
    return [...list].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    )
  }, [menu])

  const items = useMemo(() => menu?.items ?? [], [menu])

  const visibleItems = useMemo(() => {
    if (activeCategory === 'all') return items
    return items.filter((item) => String(item.categoryId) === activeCategory)
  }, [activeCategory, items])

  const cartLines = useMemo(() => {
    return Object.entries(cart)
      .map(([itemId, quantity]) => {
        const item = items.find((row) => buildCartKey(row.id) === itemId)
        if (!item) return null
        return { item, quantity }
      })
      .filter(Boolean)
  }, [cart, items])

  const cartCount = cartLines.reduce((sum, line) => sum + line.quantity, 0)
  const cartSubtotal = cartLines.reduce(
    (sum, line) => sum + line.item.price * line.quantity,
    0
  )

  const buildOrderPayload = () => ({
    organizationUuid,
    sectionName: menuKey,
    venueUuid: venueUuid || undefined,
    eventUuid: eventUuid || undefined,
    fulfillmentType,
    rowName: isPickup ? undefined : rowName,
    seatName: isPickup ? undefined : seatName,
    items: cartLines.map(({ item, quantity }) => ({
      itemId: item.id,
      quantity,
    })),
  })

  useEffect(() => {
    if (!cartOpen || !cartLines.length) {
      setPricing(null)
      return undefined
    }

    let cancelled = false
    setPricingLoading(true)

    getPublicMenuPricing(buildOrderPayload())
      .then((res) => {
        if (!cancelled) setPricing(res.data?.pricing ?? null)
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) setPricing(null)
      })
      .finally(() => {
        if (!cancelled) setPricingLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    cartOpen,
    cartLines,
    organizationUuid,
    menuKey,
    venueUuid,
    eventUuid,
    rowName,
    seatName,
    fulfillmentType,
    isPickup,
  ])

  const displayTotal = pricing?.total ?? cartSubtotal

  const addToCart = (item) => {
    const key = buildCartKey(item.id)
    setCart((current) => ({
      ...current,
      [key]: (current[key] ?? 0) + 1,
    }))
  }

  const updateQuantity = (itemId, delta) => {
    const key = buildCartKey(itemId)
    setCart((current) => {
      const nextQty = (current[key] ?? 0) + delta
      if (nextQty <= 0) {
        const { [key]: _, ...rest } = current
        return rest
      }
      return { ...current, [key]: nextQty }
    })
  }

  const resetCheckout = () => {
    setCheckoutStep('review')
    setClientSecret('')
    setPaymentIntentId('')
    setStripePromise(null)
    setLoadingPayment(false)
    setPricing(null)
  }

  const closeCart = () => {
    if (submitting) return
    setCartOpen(false)
    resetCheckout()
  }

  const finalizeOrder = async (intentId = paymentIntentId) => {
    const res = await submitPublicMenuOrder({
      ...buildOrderPayload(),
      paymentIntentId: intentId || undefined,
    })
    setOrderConfirmation(res.data)
    setActiveOrder({
      orderNumber: res.data.orderNumber,
      fulfillmentStatus: res.data.fulfillmentStatus || 'received',
      fulfillmentLabel: 'Order received',
      seat: res.data.seat,
    })
    sessionStorage.setItem(
      getActiveOrderStorageKey(organizationUuid, menuKey, venueUuid, eventUuid),
      res.data.orderNumber
    )
    setCart({})
    setCartOpen(false)
    resetCheckout()
  }

  const handleContinueToPayment = async () => {
    if (!cartLines.length || loadingPayment || submitting) return
    setLoadingPayment(true)
    setError('')
    try {
      const res = await createPublicMenuPaymentIntent(buildOrderPayload())
      const payment = res.data

      if (payment.skipPayment) {
        setSubmitting(true)
        try {
          await finalizeOrder()
        } catch (err) {
          console.error(err)
          setError(
            err?.response?.data?.error?.message ||
              'We could not place your order. Please try again.'
          )
        } finally {
          setSubmitting(false)
        }
        return
      }

      const stripeAccount = payment.stripeAccount || undefined
      const stripe = stripeAccount
        ? await loadStripe(process.env.REACT_APP_STRIPE_KEY, { stripeAccount })
        : await loadStripe(process.env.REACT_APP_STRIPE_KEY)

      setStripePromise(stripe)
      setClientSecret(payment.clientSecret)
      setPaymentIntentId(payment.paymentIntentId)
      if (payment.pricing) setPricing(payment.pricing)
      setCheckoutStep('payment')
    } catch (err) {
      console.error(err)
      setError(
        err?.response?.data?.error?.message ||
          'Unable to start checkout. Please try again.'
      )
    } finally {
      setLoadingPayment(false)
    }
  }

  const handlePaymentSucceeded = async (intentId) => {
    if (submitting) return
    setSubmitting(true)
    setError('')
    try {
      await finalizeOrder(intentId)
    } catch (err) {
      console.error(err)
      setError(
        err?.response?.data?.error?.message ||
          'Payment succeeded but we could not place your order. Please contact support.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const orgName = menu?.organization?.name ?? 'In-Suite Dining'
  const pickupLabel = locationName || menu?.location?.name || 'this location'
  const seatLabel = isPickup
    ? null
    : [menu?.seat?.section, menu?.seat?.row, menu?.seat?.seat]
        .filter(Boolean)
        .join(' · ')
  const activeStepIndex = getStatusStepIndex(activeOrder?.fulfillmentStatus)
  const isOrderComplete = ['delivered', 'cancelled'].includes(
    activeOrder?.fulfillmentStatus
  )

  if (loading) {
    return (
      <div className="luxury-menu luxury-menu--loading">
        <div className="luxury-menu__loader">
          <span className="luxury-menu__loader-ring" />
          <p>Preparing your menu</p>
        </div>
      </div>
    )
  }

  if (error && !menu) {
    return (
      <div className="luxury-menu luxury-menu--error">
        <p>{error}</p>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>
          {orgName} | {isPickup ? 'Menu' : 'Suite Menu'}
        </title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="true"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Inter:wght@300;400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="luxury-menu">
        <header className="luxury-menu__hero">
          <div className="luxury-menu__hero-overlay" />
          <div className="luxury-menu__hero-content">
            <p className="luxury-menu__eyebrow">
              {isPickup ? 'Order for Pickup' : 'Curated In-Suite Service'}
            </p>
            <h1>{orgName}</h1>
            {isPickup ? (
              <p className="luxury-menu__seat">
                Pick up at <span>{pickupLabel}</span>
              </p>
            ) : (
              seatLabel && (
                <p className="luxury-menu__seat">
                  Delivering to <span>{seatLabel}</span>
                </p>
              )
            )}
          </div>
        </header>

        {activeOrder && (
          <div
            className={`luxury-menu__tracker${isOrderComplete ? ' luxury-menu__tracker--complete' : ''}`}
          >
            <div className="luxury-menu__tracker-header">
              <div>
                <p className="luxury-menu__tracker-eyebrow">Active order</p>
                <strong>{activeOrder.orderNumber}</strong>
              </div>
              <span className="luxury-menu__tracker-status">
                {activeOrder.fulfillmentLabel ||
                  statusSteps[activeStepIndex]?.label}
              </span>
            </div>
            <ol className="luxury-menu__tracker-steps">
              {statusSteps.map((step, index) => (
                <li
                  key={step.key}
                  className={`luxury-menu__tracker-step${
                    index <= activeStepIndex ? ' is-complete' : ''
                  }${index === activeStepIndex ? ' is-current' : ''}`}
                >
                  <span>{step.label}</span>
                </li>
              ))}
            </ol>
            {!isOrderComplete && activeOrder.estimatedMinutes > 0 && (
              <p className="luxury-menu__tracker-eta">
                Estimated {activeOrder.estimatedMinutes} min
              </p>
            )}
            {isOrderComplete && (
              <button
                type="button"
                className="luxury-menu__tracker-dismiss"
                onClick={() => setActiveOrder(null)}
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {categories.length > 0 && (
          <nav className="luxury-menu__categories" aria-label="Menu categories">
            <button
              type="button"
              className={`luxury-menu__category${activeCategory === 'all' ? ' is-active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`luxury-menu__category${activeCategory === category.id ? ' is-active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </nav>
        )}

        <main className="luxury-menu__content">
          {visibleItems.length === 0 ? (
            <div className="luxury-menu__empty">
              <h2>Menu unavailable</h2>
              <p>
                There are no items available for your location at this time.
              </p>
            </div>
          ) : (
            <section className="luxury-menu__grid">
              {visibleItems.map((item) => {
                const imageUrl = resolveMediaUrl(item.imageUrl)
                const quantity = cart[buildCartKey(item.id)] ?? 0
                return (
                  <article key={item.id} className="luxury-menu__card">
                    <div className="luxury-menu__card-media">
                      {imageUrl ? (
                        <img src={imageUrl} alt={item.name} loading="lazy" />
                      ) : (
                        <div className="luxury-menu__card-placeholder" />
                      )}
                    </div>
                    <div className="luxury-menu__card-body">
                      <div className="luxury-menu__card-header">
                        <h3>{item.name}</h3>
                        <span className="luxury-menu__price">
                          {formatCurrency(item.price)}
                        </span>
                      </div>
                      {item.description && (
                        <p className="luxury-menu__description">
                          {item.description}
                        </p>
                      )}
                      <div className="luxury-menu__card-meta">
                        {item.isAlcohol && (
                          <span className="luxury-menu__tag">21+</span>
                        )}
                        {item.calories ? (
                          <span className="luxury-menu__tag">
                            {item.calories} cal
                          </span>
                        ) : null}
                      </div>
                      <div className="luxury-menu__card-actions">
                        {quantity > 0 ? (
                          <div className="luxury-menu__qty">
                            <button
                              type="button"
                              aria-label={`Decrease ${item.name}`}
                              onClick={() => updateQuantity(item.id, -1)}
                            >
                              −
                            </button>
                            <span>{quantity}</span>
                            <button
                              type="button"
                              aria-label={`Increase ${item.name}`}
                              onClick={() => updateQuantity(item.id, 1)}
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="luxury-menu__add-btn"
                            onClick={() => addToCart(item)}
                          >
                            Add to order
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                )
              })}
            </section>
          )}
        </main>

        {cartCount > 0 && (
          <button
            type="button"
            className="luxury-menu__cart-bar"
            onClick={() => setCartOpen(true)}
          >
            <span>
              {cartCount} item{cartCount === 1 ? '' : 's'}
            </span>
            <span>{formatCurrency(displayTotal)}</span>
            <span className="luxury-menu__cart-bar-cta">Review order</span>
          </button>
        )}

        {cartOpen && (
          <div
            className="luxury-menu__drawer-backdrop"
            onClick={closeCart}
            role="presentation"
          />
        )}

        <aside
          className={`luxury-menu__drawer${cartOpen ? ' is-open' : ''}`}
          aria-hidden={!cartOpen}
        >
          <div className="luxury-menu__drawer-header">
            <h2>{checkoutStep === 'payment' ? 'Payment' : 'Your order'}</h2>
            <button
              type="button"
              className="luxury-menu__drawer-close"
              onClick={closeCart}
              aria-label="Close order summary"
            >
              ×
            </button>
          </div>

          <div className="luxury-menu__drawer-body">
            {checkoutStep === 'review' &&
              cartLines.map(({ item, quantity }) => (
                <div key={item.id} className="luxury-menu__drawer-line">
                  <div>
                    <strong>{item.name}</strong>
                    <p>{formatCurrency(item.price)} each</p>
                  </div>
                  <div className="luxury-menu__qty">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, -1)}
                      aria-label={`Decrease ${item.name}`}
                    >
                      −
                    </button>
                    <span>{quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, 1)}
                      aria-label={`Increase ${item.name}`}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}

            {checkoutStep === 'payment' && (
              <div className="luxury-menu__payment-panel">
                <MenuPricingBreakdown
                  pricing={pricing}
                  loading={pricingLoading}
                />
                <p className="luxury-menu__payment-copy">
                  Complete payment to place your order. Your card is charged
                  securely before the kitchen receives it.
                </p>
                <MenuPayment
                  stripePromise={stripePromise}
                  clientSecret={clientSecret}
                  total={formatCurrency(pricing?.total ?? cartSubtotal)}
                  submitting={submitting}
                  onPaymentReady={() => {}}
                  onSubmit={handlePaymentSucceeded}
                />
              </div>
            )}
          </div>

          <div className="luxury-menu__drawer-footer">
            {checkoutStep === 'review' && (
              <MenuPricingBreakdown
                pricing={pricing}
                loading={pricingLoading}
              />
            )}
            <div className="luxury-menu__drawer-total">
              <span>Total</span>
              <span>{formatCurrency(displayTotal)}</span>
            </div>
            {error && <p className="luxury-menu__drawer-error">{error}</p>}
            {checkoutStep === 'review' && (
              <>
                <button
                  type="button"
                  className="luxury-menu__submit-btn"
                  disabled={loadingPayment || submitting || !cartLines.length}
                  onClick={handleContinueToPayment}
                >
                  {loadingPayment
                    ? 'Preparing checkout…'
                    : 'Continue to payment'}
                </button>
                <p className="luxury-menu__drawer-note">
                  Payment is collected securely before your order is sent to the
                  kitchen.
                </p>
              </>
            )}
            {checkoutStep === 'payment' && (
              <button
                type="button"
                className="luxury-menu__drawer-back-btn"
                disabled={submitting}
                onClick={() => {
                  setCheckoutStep('review')
                  setClientSecret('')
                  setPaymentIntentId('')
                  setStripePromise(null)
                }}
              >
                Back to order
              </button>
            )}
          </div>
        </aside>

        {orderConfirmation && (
          <div className="luxury-menu__confirmation">
            <div className="luxury-menu__confirmation-card">
              <p className="luxury-menu__eyebrow">Order received</p>
              <h2>Thank you</h2>
              <p>
                Your order <strong>{orderConfirmation.orderNumber}</strong> is
                on its way to{' '}
                <strong>
                  {[
                    orderConfirmation.seat?.section,
                    orderConfirmation.seat?.row,
                    orderConfirmation.seat?.seat,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </strong>
                . Track progress at the top of the menu.
              </p>
              <button
                type="button"
                className="luxury-menu__submit-btn"
                onClick={() => setOrderConfirmation(null)}
              >
                Continue browsing
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
